import type { IdealItemDto, Site, SitePhoto, SiteRemovalReason, SiteUse, StepGuideDto } from '@bk/contracts';
import { DutyFacts } from './Duty';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { api, errorMessage } from '../../../lib/api';
import { useTenant, useTenantQuery } from '../../../lib/useTenant';
import { Button, ErrorText } from '../../../ui';
import { StepIntro } from './Illustration';
import { BrainNotes } from './StepReview';
import { AddressInput, addressLine } from '../AddressInput';
import { SiteStreetView } from './Maps';
import { DoneBand, Feeds, Glance, Pill, Row, RowGroup, useRows } from './Summary';
import { ItemBase } from './Labels';
import { LibrarySources } from './LibrarySources';

const FROM: Record<Site['source'], string> = {
  visura: 'dalla visura',
  persona: 'detta da te',
  documento: 'da un tuo documento',
  posta: 'dalla posta',
  sito: 'dal vostro sito',
  registro: 'da una pagina pubblica',
  online: 'da una pagina online sulla sede',
  supposto: 'supposto dal cervello',
};

/** L'indirizzo come lo scrive una persona: «Via Roma 1, 20100 Milano (MI)». */
export function where(s: Site): string | null {
  const town = [s.postal_code, s.municipality].filter(Boolean).join(' ');
  const place = [town, s.province && s.province !== s.municipality ? `(${s.province})` : null]
    .filter(Boolean)
    .join(' ');
  return [s.address, place].filter(Boolean).join(', ') || null;
}

/** Si fa qualcosa per i clienti: le attività hanno senso solo qui (ADR 0029). */
const operative = (s: Site) => s.use !== 'interna' && s.use !== 'domicilio';

/** Da dove viene la sede; se è una pagina, si apre con un tocco (ADR 0070). */
function From({ s }: { s: Site }) {
  const url = s.source_ref && /^https?:\/\//.test(s.source_ref) ? s.source_ref : null;
  return url ? (
    <a href={url} target="_blank" rel="noreferrer" className="underline decoration-dotted underline-offset-2 hover:text-accent">
      {FROM[s.source]}
    </a>
  ) : (
    <>{FROM[s.source]}</>
  );
}

/** I numeri della sede trovati sul web, in una riga: ognuno porta alla pagina che lo dice (ADR 0070, 0072). */
function Facts({ s, className = '' }: { s: Site; className?: string }) {
  const items: ReactNode[] = s.in_breve.map((f) =>
    f.url ? (
      <a key={f.testo} href={f.url} target="_blank" rel="noreferrer" className="hover:text-accent hover:underline">
        {f.testo}
      </a>
    ) : (
      <span key={f.testo}>{f.testo}</span>
    ),
  );
  if (!items.length) return null;
  return (
    <p className={className}>
      {items.map((x, k) => (
        <span key={k}>
          {k > 0 && ' · '}
          {x}
        </span>
      ))}
    </p>
  );
}

/** Una data della visura come la legge una persona: «30/06/2026». */
const day = (d: string | null) => (d && /^\d{4}-\d{2}-\d{2}/.test(d) ? d.slice(0, 10).split('-').reverse().join('/') : null);

/** Il nome della sede da mostrare: un nome del registro («Unità Locale n. RM/1») lascia il posto a «Sede di Roma». */
export function titleOf(s: Site): string {
  return /^unit[aà]'? local/i.test(s.name) && s.municipality ? `Sede di ${s.municipality}` : s.name;
}

/**
 * Da dove viene il numero degli addetti della sede e a che data (ADR 0099), in parole: «dalla visura, al 30/06/2026»,
 * «detto da te», «stima del consulente, da confermare». Vuoto se la fonte non è nota.
 */
export function staffSource(s: Pick<Site, 'employees_source' | 'employees_ref' | 'employees_as_of'>): string {
  const ref = s.employees_ref ?? '';
  const from: Record<NonNullable<Site['employees_source']>, string> = {
    persona: 'detto da te',
    visura: 'dalla visura',
    registro: 'dal registro delle imprese',
    documento: /visura/i.test(ref) ? 'dalla visura' : 'da un tuo documento',
    comune: /meno/.test(ref) ? `dalla visura: ${ref}` : 'dalla visura, per il comune',
    posta: 'da una tua mail',
    sito: 'dal vostro sito',
    online: 'dal web',
    supposto: 'stima del consulente, da confermare',
  };
  const al = s.employees_source !== 'persona' && s.employees_source !== 'supposto' ? day(s.employees_as_of) : null;
  return [s.employees_source ? from[s.employees_source] : '', al ? `al ${al}` : ''].filter(Boolean).join(', ');
}

/** Gli addetti come li scrive la visura (ADR 0072): per la sede, oppure per il comune quando lì ci sono più sedi. */
function staffLine(s: Site, inTown: number): string {
  const c = s.addetti_comune;
  const al = day(c?.al ?? null);
  if (c && inTown > 1)
    return `${c.addetti} nel comune di ${s.municipality}${al ? ` al ${al}` : ''}, per tutte le ${inTown} sedi del comune${s.employees != null ? ` (qui ${s.employees}${s.employees_source && s.employees_source !== 'comune' ? `, ${staffSource(s)}` : ''})` : ''}`;
  // Il numero della sede con la sua fonte (ADR 0099); se è diverso da quello della visura per il comune, li dice tutti e due.
  if (s.employees != null)
    return `${s.employees}${staffSource(s) ? ` (${staffSource(s)})` : s.employees_as_of ? ` al ${day(s.employees_as_of)}` : ''}${c && c.addetti !== s.employees ? `; la visura ne scrive ${c.addetti} nel comune${al ? ` al ${al}` : ''}` : ''}`;
  if (c) return `${c.addetti}${al ? ` al ${al}` : ''}`;
  return 'la visura non li scrive per questa sede';
}

/** Una riga «etichetta: valore» dei blocchi della sede. */
function Line({ k, children }: { k: string; children: ReactNode }) {
  return (
    <p className="grid grid-cols-[9.5rem_minmax(0,1fr)] gap-x-2 text-sm leading-snug">
      <span className="text-ink/45">{k}</span>
      <span className="text-ink/80">{children}</span>
    </p>
  );
}

/**
 * Ciò che si sa della sede, in due blocchi (ADR 0072): prima ciò che dice la visura, fonte certa (sede legale o unità
 * locale, codici ATECO, addetti); poi ciò che il cervello ha trovato sul web e ritiene affidabile (marchi commerciali,
 * cos'è, numeri), ognuno con la sua pagina. Poi le note del titolare.
 */
export function SiteDetails({ s, inTown, manager }: { s: Site; inTown: number; manager: boolean }) {
  const codes = [s.ateco_primary, ...s.ateco_secondary].filter((c): c is string => !!c);
  const web = s.marchi.length > 0 || !!s.sintesi || s.in_breve.length > 0 || s.allo_stesso_indirizzo.length > 0;
  return (
    <div className="mt-2 space-y-2">
      {s.visura ? (
        <div className="space-y-0.5 rounded-xl bg-emerald-50/60 px-3 py-2">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-800/80">Dalla visura · {s.visura}</p>
          <Line k="Codici ATECO">{codes.length ? codes.join('; ') : 'la visura non li scrive per questa sede'}</Line>
          <Line k="Addetti">{staffLine(s, inTown)}</Line>
        </div>
      ) : (
        <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Questa sede non è nella visura: <From s={s} />. Se non è vostra, toglila o dillo al cervello qui sotto.
        </p>
      )}
      {web && (
        <div className="space-y-0.5 rounded-xl bg-accent/[0.04] px-3 py-2">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-accent/80">Dal web · verificato dal cervello</p>
          {s.marchi.length > 0 && <Line k="Marchi commerciali">{s.marchi.join(' · ')}</Line>}
          {s.sintesi && <Line k="Cos’è">{s.sintesi}</Line>}
          {s.allo_stesso_indirizzo.length > 0 && (
            <Line k="Allo stesso indirizzo">
              {s.allo_stesso_indirizzo.map((n) => `${n.nome}${n.categoria ? ` (${n.categoria.toLowerCase()})` : ''}`).join(' · ')}
              <span className="text-ink/50"> — un’altra impresa: il consulente ne tiene conto in ogni passo</span>
            </Line>
          )}
          {s.in_breve.length > 0 && (
            <Line k="In numeri">
              <Facts s={s} />
            </Line>
          )}
        </div>
      )}
      <Notes s={s} manager={manager} />
    </div>
  );
}

/**
 * Cosa non va in questa sede, detto a parole al cervello (ADR 0072): la nota resta sulla sede e il cervello ne tiene
 * conto in tutti i passi.
 */
function Notes({ s, manager }: { s: Site; manager: boolean }) {
  const { t } = useTenant();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const add = useMutation({
    mutationFn: () => api(t(`/sites/${s.id}/notes`), { body: { testo: text.trim() } }),
    onSuccess: () => {
      setText('');
      setOpen(false);
      void qc.invalidateQueries();
    },
  });
  const remove = useMutation({
    mutationFn: (k: number) => api(t(`/sites/${s.id}/notes/${k}`), { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries(),
  });
  return (
    <div className="space-y-1">
      {s.note.map((n, k) => (
        <p key={k} className="rounded-xl bg-black/[0.03] px-3 py-1.5 text-sm text-ink/75">
          <span className="text-ink/45">Hai detto al cervello: </span>
          {n.testo}
          {manager && (
            <button className="ml-2 text-xs text-ink/40 hover:text-red-700 hover:underline" disabled={remove.isPending} onClick={() => remove.mutate(k)}>
              togli
            </button>
          )}
        </p>
      ))}
      {manager &&
        (open ? (
          <form
            className="space-y-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (text.trim().length > 1) add.mutate();
            }}
          >
            <textarea
              autoFocus
              rows={2}
              className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
              placeholder="Es. qui c’è anche il nostro ristorante, oppure: questa sede è chiusa da giugno"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={add.isPending || text.trim().length < 2}>
                Dillo al cervello
              </Button>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Annulla
              </Button>
              <span className="text-xs text-ink/45">Lo terrà presente in tutti i passi.</span>
            </div>
            {add.error && <ErrorText>{errorMessage(add.error)}</ErrorText>}
          </form>
        ) : (
          <button className="text-xs font-medium text-accent hover:underline" onClick={() => setOpen(true)}>
            ✎ Qualcosa non va in questa sede? Dillo al cervello
          </button>
        ))}
    </div>
  );
}

/** La foto della sede dalla sua scheda sulle mappe (ADR 0070), se porta uno dei suoi nomi. */
export function useSitePhoto(s: Site): SitePhoto | null {
  const q = useTenantQuery<{ foto: SitePhoto | null }>(
    ['sites', s.id, 'photo', s.name, s.address],
    `/sites/${s.id}/photo`,
    { enabled: operative(s) && !!s.address },
  );
  return q.data?.foto ?? null;
}

/** La foto con il nome di chi l'ha scattata, come chiede Google. */
export function Photo({ photo, alt, className = '', credit = true }: { photo: SitePhoto; alt: string; className?: string; credit?: boolean }) {
  const by = photo.autori[0];
  return (
    <div className={`relative overflow-hidden bg-black/[0.04] ${className}`}>
      <img src={photo.url} alt={alt} loading="lazy" referrerPolicy="no-referrer" className="absolute inset-0 h-full w-full object-cover" />
      {credit && by && (
        <span className="absolute bottom-1 right-1 max-w-[90%] truncate rounded bg-black/45 px-1.5 py-0.5 text-[10px] text-white">
          Foto:{' '}
          {by.url ? (
            <a href={by.url} target="_blank" rel="noreferrer" className="hover:underline">
              {by.nome}
            </a>
          ) : (
            by.nome
          )}
        </span>
      )}
    </div>
  );
}

/** Entra scorrendo dal lato verso cui si va. */
export function useSlideIn(dir: number) {
  const box = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    box.current?.animate(
      [
        { opacity: 0, transform: `translateX(${dir * 32}px)` },
        { opacity: 1, transform: 'none' },
      ],
      { duration: 220, easing: 'ease-out' },
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return box;
}

/**
 * Passo «Sedi» della configurazione guidata, giocato come «L'impresa»: «sono queste le tue sedi?». Una sede alla volta
 * a scorrimento, vista dalla strada: un tocco per dire che è giusta (e si passa alla successiva), oppure la correggi o
 * la togli. A conferme finite le schede lasciano il posto al riepilogo: la festa in testa e una riga per sede con i
 * suoi dettagli, che al tocco si apre nella scheda completa. Ruoli e figure stanno in «Organizzazione e ruoli».
 */
export function SitesStep({
  next,
  onNext,
}: {
  next: { titolo: string } | null;
  onNext: () => void;
}) {
  const { isManager } = useTenant();
  const sites = useTenantQuery<{ data: Site[] }>(['sites'], '/sites');
  // Le domande del cervello che ha compilato sedi e attività (ADR 0062): la stessa guida che legge il passo.
  const guide = useTenantQuery<StepGuideDto>(['guide', 'sedi'], '/onboarding/guide/sedi');
  const [adding, setAdding] = useState(false);
  const all = sites.data?.data ?? [];
  const list = all.filter((s) => s.status !== 'chiusa');
  const removed = all.filter((s) => s.status === 'chiusa');
  const done = list.filter((s) => s.status === 'attiva').length;
  const allDone = !!list.length && done === list.length;
  // Tutte confermate: il riepilogo al posto delle schede (ADR 0033). «Rivedi il passo» rimette le schede.
  const [review, setReview] = useState(false);
  const summary = allDone && !review;
  const rows = useRows();
  const uses = { operativa: 0, interna: 0, domicilio: 0 };
  for (const s of list) if (s.use) uses[s.use]++;
  const staff = list.reduce((n, s) => n + (s.employees ?? 0), 0);
  // Le sedi aperte dello stesso comune: la visura scrive gli addetti per comune (ADR 0072).
  const inTown = (x: Site) => list.filter((y) => !!x.municipality && y.municipality === x.municipality).length;

  // La scheda aperta si ricorda per sede: l'elenco cambia quando una sede si toglie o si aggiunge.
  const [cur, setCur] = useState<string | null>(null);
  const [want, setWant] = useState<string | null>(null);
  const [dir, setDir] = useState(1);
  // Si parte dalla prima sede ancora da confermare; poi decide la persona.
  useEffect(() => {
    if (cur || !list.length) return;
    setCur((list.find((s) => s.status !== 'attiva') ?? list[0]!).id);
  }, [!!sites.data]); // eslint-disable-line react-hooks/exhaustive-deps
  // Dopo una risposta si va alla sede giusta appena c'è (una sede aggiunta arriva con il ricaricamento).
  useEffect(() => {
    if (want && list.some((s) => s.id === want)) {
      setDir(1);
      setCur(want);
      setWant(null);
    }
  }, [want, list.map((s) => s.id).join('|')]); // eslint-disable-line react-hooks/exhaustive-deps
  const found = list.findIndex((s) => s.id === cur);
  const at = found >= 0 ? found : 0;
  const show = (k: number) => {
    if (k < 0 || k >= list.length || k === at) return;
    setDir(k > at ? 1 : -1);
    setCur(list[k]!.id);
  };
  // Dopo «Giusta» o «Non è nostra» si va alla prossima sede da confermare, se c'è.
  const onward = (id: string) => {
    const k = list.findIndex((s) => s.id === id);
    const later = [...list.slice(k + 1), ...list.slice(0, k)].find((s) => s.status !== 'attiva');
    if (later) setWant(later.id);
    else if (list[k + 1]) setWant(list[k + 1]!.id);
    else if (k > 0) setWant(list[k - 1]!.id);
  };
  // Frecce della tastiera per scorrere le sedi, se non si sta scrivendo.
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (summary || el?.closest('input, textarea, select, [contenteditable]')) return;
      if (e.key === 'ArrowRight') show(at + 1);
      if (e.key === 'ArrowLeft') show(at - 1);
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  });
  const touch = useRef<number | null>(null);
  const site = list[at];

  return (
    <div className="pb-8 pt-2">
      <StepIntro
        step="sedi"
        title={
          summary
            ? list.length > 1
              ? 'Le tue sedi'
              : 'La tua sede'
            : list.length > 1
              ? 'Sono queste le tue sedi?'
              : 'È questa la tua sede?'
        }
      >
        <p className="mt-1 text-sm text-ink/60">
          {summary
            ? list.length > 1
              ? 'Ecco dove lavora la tua azienda, in un colpo d’occhio. Per cambiare una sede tocca «Modifica»: si apre solo quella.'
              : 'Ecco dove lavora la tua azienda. Per cambiarla tocca «Modifica».'
            : list.length > 1
              ? 'Una sede alla volta: dimmi se è giusta. Un tocco per ognuna, e se qualcosa non va la correggi tu.'
              : list.length
                ? 'Ecco dove lavora la tua azienda. Dimmi se è giusto, e se qualcosa non va la correggi tu.'
                : 'Non ho ancora trovato dove lavora la tua azienda: aggiungi la sede qui sotto.'}
        </p>
        {list.length > 0 && !summary && (
          <div
            className="mt-3 flex flex-wrap items-center gap-1.5"
            aria-label={`${done} di ${list.length} confermate`}
          >
            {list.map((s, k) => {
              const here = summary ? rows.open === s.id : k === at;
              return (
                <button
                  key={s.id}
                  title={s.name}
                  aria-label={`${s.name}${s.status === 'attiva' ? ' (confermata)' : ''}`}
                  aria-current={here ? 'true' : undefined}
                  onClick={() => (summary ? rows.toggle(s.id) : show(k))}
                  className={`h-2 w-8 rounded-full transition-colors ${s.status === 'attiva' ? 'bg-emerald-500' : 'bg-black/10'} ${here ? 'ring-2 ring-accent/50 ring-offset-1' : ''}`}
                />
              );
            })}
            <span className="ml-2 text-xs tabular-nums text-ink/55">
              {done} di {list.length}
            </span>
          </div>
        )}
      </StepIntro>
      <BrainNotes step="sedi" d={guide.data?.ideale ?? null} what="le sedi" />

      {sites.isLoading && (
        <p className="py-6 text-center text-sm text-ink/50">Raccolgo le sedi della tua azienda…</p>
      )}

      {review && allDone && (
        <p className="mt-4 text-center">
          <button className="text-sm text-accent hover:underline" onClick={() => setReview(false)}>
            ← Torna al riepilogo
          </button>
        </p>
      )}

      {site && !summary && (
        <>
          {list.length > 1 && (
            <div className="mt-6 flex items-center gap-2">
              <button
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-lg shadow-sm ring-1 ring-black/5 transition hover:ring-accent/40 disabled:opacity-30"
                aria-label="Sede precedente"
                disabled={at === 0}
                onClick={() => show(at - 1)}
              >
                ‹
              </button>
              <p className="min-w-0 flex-1 text-center text-xs text-ink/55">
                <span className="font-semibold tabular-nums">{at + 1}</span> di{' '}
                <span className="tabular-nums">{list.length}</span>
              </p>
              <button
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-lg shadow-sm ring-1 ring-black/5 transition hover:ring-accent/40 disabled:opacity-30"
                aria-label="Sede successiva"
                disabled={at === list.length - 1}
                onClick={() => show(at + 1)}
              >
                ›
              </button>
            </div>
          )}
          <div
            className={list.length > 1 ? 'mt-3' : 'mt-6'}
            onTouchStart={(e) => (touch.current = e.touches[0]?.clientX ?? null)}
            onTouchEnd={(e) => {
              const x = e.changedTouches[0]?.clientX;
              if (touch.current != null && x != null && Math.abs(x - touch.current) > 60)
                show(x < touch.current ? at + 1 : at - 1);
              touch.current = null;
            }}
          >
            <SiteCard
              key={site.id}
              s={site}
              inTown={inTown(site)}
              dir={dir}
              manager={isManager}
              onStatus={(v) => v !== 'da_confermare' && onward(site.id)}
            />
          </div>
          {at < list.length - 1 && (
            <button
              className="mt-3 w-full rounded-2xl bg-accent/[0.06] px-4 py-3 text-sm font-medium text-accent transition hover:bg-accent/10"
              onClick={() => show(at + 1)}
            >
              Avanti: {list[at + 1]!.name} →
            </button>
          )}
        </>
      )}

      {summary && (
        <div className="mt-6">
          <DoneBand
            title={list.length > 1 ? 'Le sedi sono a posto' : 'La sede è a posto'}
            onReview={isManager ? () => setReview(true) : null}
          />
          <Glance
            tiles={[
              [list.length, list.length === 1 ? 'sede' : 'sedi in tutto'],
              uses.operativa > 0 && [uses.operativa, 'dove si lavora per i clienti'],
              uses.interna > 0 && [uses.interna, 'solo direzione e amministrazione'],
              uses.domicilio > 0 && [uses.domicilio, 'solo un indirizzo'],
              staff > 0 && [staff, staff === 1 ? 'persona ci lavora' : 'persone ci lavorano'],
            ]}
          />
          {/* Prima dove si lavora per i clienti, poi uffici e indirizzi (ADR 0071): ognuno sa dove cercare. */}
          {[
            { title: 'Dove lavorate per i clienti', items: list.filter(operative) },
            { title: list.some(operative) ? 'Uffici e indirizzi' : 'Le vostre sedi', items: list.filter((s) => !operative(s)) },
          ]
            .filter((g) => g.items.length)
            .map((g, k) => (
              <RowGroup
                key={g.title}
                title={g.title}
                sub={k === 0 && isManager ? 'tocca «Modifica» per cambiare una sola sede' : undefined}
              >
                {[...g.items]
                  .sort((a, b) => Number(b.legal) - Number(a.legal) || a.name.localeCompare(b.name, 'it'))
                  .map((s) => (
                    <SiteRow
                      key={s.id}
                      s={s}
                      inTown={inTown(s)}
                      manager={isManager}
                      open={rows.open === s.id}
                      edited={rows.edited === s.id}
                      onEdit={isManager ? () => rows.toggle(s.id) : null}
                      editor={
                        <SiteCard
                          s={s}
                          inTown={inTown(s)}
                          dir={0}
                          edit
                          manager={isManager}
                          onSaved={() => rows.close(true)}
                          onStatus={(v) => {
                            rows.close(v === 'attiva');
                            // Tornata da confermare: si torna alle schede, su di lei.
                            if (v === 'da_confermare') setWant(s.id);
                          }}
                        />
                      }
                    />
                  ))}
              </RowGroup>
            ))}
        </div>
      )}

      {isManager &&
        (adding ? (
          <div className="mt-3 rounded-2xl border-2 border-accent/30 bg-white px-4 py-3">
            <p className="mb-2 text-sm font-medium">Una sede in più</p>
            <SiteEdit
              onDone={(id) => {
                setAdding(false);
                if (id) setWant(id);
              }}
            />
          </div>
        ) : (
          <button
            className="mt-3 w-full rounded-2xl border-2 border-dashed border-black/10 px-4 py-3 text-sm text-ink/60 transition hover:border-accent hover:text-accent"
            onClick={() => setAdding(true)}
          >
            ＋ {list.length ? 'Manca una sede? Aggiungila' : 'Aggiungi la sede'}
          </button>
        ))}

      {removed.length > 0 && (
        <p className="mt-4 text-xs text-ink/45">
          Tolte da te, non tornano più:{' '}
          {removed.map((s, k) => (
            <span key={s.id}>
              {k > 0 && ', '}
              {s.name}
              {s.tolta?.motivo && <span className="text-ink/40"> ({REMOVED_WORD[s.tolta.motivo]})</span>}{' '}
              {isManager && <Restore s={s} />}
            </span>
          ))}
        </p>
      )}

      {summary && <Feeds steps={['Attività economiche', 'Impianti e attrezzature']} />}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <span className="flex-1" />
        {next && (
          <Button onClick={onNext}>
            Avanti<span className="hidden sm:inline">: {next.titolo}</span> →
          </Button>
        )}
      </div>
    </div>
  );
}

function SiteCard({
  s,
  inTown,
  dir,
  edit = false,
  manager,
  onStatus,
  onSaved,
}: {
  s: Site;
  dir: number;
  /** Si apre subito in modifica. */
  edit?: boolean;
  manager: boolean;
  /** Le correzioni sono salvate (nel riepilogo la riga torna chiusa). */
  onSaved?: () => void;
  /** La persona ha risposto (giusta, non è nostra, annulla conferma): chi la mostra decide dove andare. */
  onStatus: (v: Site['status']) => void;
  /** Quante sedi aperte ci sono nel suo comune (per gli addetti della visura scritti per comune). */
  inTown: number;
}) {
  const { t } = useTenant();
  const qc = useQueryClient();
  const box = useSlideIn(dir);
  const [editing, setEditing] = useState(edit);
  const status = useMutation({
    mutationFn: (v: Site['status'] | Removal) =>
      api(t(`/sites/${s.id}`), {
        method: 'PATCH',
        body:
          typeof v === 'string'
            ? { status: v }
            : v === REMOVALS.solo_indirizzo
              ? { status: 'attiva', use: 'domicilio' }
              : { status: 'chiusa', motivo: v.motivo },
      }),
    onSuccess: (_, v) => {
      void qc.invalidateQueries();
      onStatus(typeof v === 'string' ? v : v === REMOVALS.solo_indirizzo ? 'attiva' : 'chiusa');
    },
  });
  // Togliere una sede chiede il perché (ADR 0094): ogni risposta ha un effetto diverso.
  const [removing, setRemoving] = useState(false);
  const confirmed = s.status === 'attiva';
  const address = where(s);
  const photo = useSitePhoto(s);
  const what = operative(s) && s.activities.length ? s.activities.map((a) => a.name).join(', ') : operative(s) ? s.activity : null;
  return (
    <div
      ref={box}
      className={`overflow-hidden rounded-2xl border-2 bg-white transition ${confirmed ? 'border-emerald-400/60' : 'border-black/5'}`}
    >
      {editing ? (
        <div className="px-4 py-3">
          <SiteEdit
            site={s}
            onDone={(_, saved) => {
              setEditing(false);
              if (saved) onSaved?.();
            }}
          />
        </div>
      ) : (
        // La vista dalla strada occupa il lato destro della scheda fino ai bordi (sopra, su schermi stretti).
        <div className="flex flex-col-reverse sm:grid sm:grid-cols-[minmax(0,1fr)_16rem]">
          <div className="min-w-0 px-4 py-3">
            <p className="text-xs text-ink/50">
              {s.icona && <span aria-hidden>{s.icona} </span>}
              {s.legal ? 'Sede principale (sede legale)' : 'Sede'}
            </p>
            <p className="text-base font-semibold">{titleOf(s)}</p>
            <p className="text-sm text-ink/55">
              {address ?? <span className="text-amber-700">Manca l’indirizzo</span>}
            </p>
            <SiteDetails s={s} inTown={inTown} manager={manager} />
            {what && <p className="mt-2 text-xs text-ink/55">Cosa si fa qui: {what}</p>}
            <p className="mt-1 text-xs text-ink/40">
              <From s={s} />
            </p>
            {manager && (
              <div className="mt-3">
                <div className="flex flex-wrap items-center gap-2">
                  {confirmed ? (
                    <span className="flex items-center gap-3 text-sm">
                      <span className="font-medium text-emerald-700">✓ Giusta</span>
                      <button
                        className="text-xs text-accent hover:underline"
                        onClick={() => setEditing(true)}
                      >
                        cambia
                      </button>
                      <button
                        className="text-xs text-ink/45 hover:text-ink/70 hover:underline disabled:opacity-50"
                        disabled={status.isPending}
                        onClick={() => status.mutate('da_confermare')}
                      >
                        annulla conferma
                      </button>
                    </span>
                  ) : (
                    <>
                      <button
                        className="rounded-full bg-emerald-50 px-4 py-1.5 text-sm font-medium text-emerald-800 transition hover:bg-emerald-100 disabled:opacity-50"
                        disabled={status.isPending}
                        onClick={() => status.mutate('attiva')}
                      >
                        👍 Giusta
                      </button>
                      <button
                        className="rounded-full bg-black/5 px-4 py-1.5 text-sm font-medium text-ink/70 transition hover:bg-black/10"
                        onClick={() => setEditing(true)}
                      >
                        ✏️ Correggi
                      </button>
                    </>
                  )}
                </div>
                {removing ? (
                  <div className="mt-2 rounded-xl bg-black/[0.03] px-3 py-2">
                    <p className="mb-1.5 text-xs font-medium text-ink/70">Perché la togli?</p>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(REMOVALS).map(([k, r]) => (
                        <button
                          key={k}
                          className="rounded-full bg-white px-3 py-1 text-xs font-medium text-ink/75 ring-1 ring-black/10 transition hover:ring-accent/40 disabled:opacity-50"
                          disabled={status.isPending}
                          title={r.spiega}
                          onClick={() => status.mutate(r)}
                        >
                          {r.titolo}
                        </button>
                      ))}
                      <button className="px-2 text-xs text-ink/45 hover:underline" onClick={() => setRemoving(false)}>
                        Annulla
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="mt-2 text-xs text-ink/45 hover:text-red-700 hover:underline disabled:opacity-50"
                    disabled={status.isPending}
                    onClick={() => setRemoving(true)}
                  >
                    Non c’è più o non è nostra
                  </button>
                )}
              </div>
            )}
          </div>
          {photo ? (
            <Photo photo={photo} alt={s.name} className="h-44 w-full sm:h-full sm:min-h-[13rem]" />
          ) : (
            <SiteStreetView site={s} className="h-44 w-full sm:h-full sm:min-h-[13rem]" />
          )}
        </div>
      )}
      {status.error && (
        <div className="px-4 pb-2">
          <ErrorText>{errorMessage(status.error)}</ErrorText>
        </div>
      )}
    </div>
  );
}

/** I tre perché di una sede tolta (ADR 0094). «È solo un indirizzo» non la toglie: nessuno ci lavora. */
type Removal = { titolo: string; spiega: string; motivo?: SiteRemovalReason };
const REMOVALS: Record<'non_esiste_piu' | 'non_nostra' | 'solo_indirizzo', Removal> = {
  non_esiste_piu: { titolo: 'Non esiste più', spiega: 'Chiusa o trasferita: il consulente lo tiene presente in tutti i passi', motivo: 'non_esiste_piu' },
  non_nostra: { titolo: 'Non è nostra', spiega: 'Le fonti si sono sbagliate: non torna più', motivo: 'non_nostra' },
  solo_indirizzo: { titolo: 'È solo un indirizzo', spiega: 'Resta, ma nessuno ci lavora: niente attività né impianti' },
};
const REMOVED_WORD: Record<SiteRemovalReason, string> = { non_esiste_piu: 'non esiste più', non_nostra: 'non è vostra' };

/** L'uso della sede (ADR 0029), come lo legge una persona. */
const USE: Record<SiteUse, string> = {
  operativa: 'si lavora per i clienti',
  interna: 'solo direzione e amministrazione',
  domicilio: 'solo un indirizzo',
};

export const USE_ICON: Record<SiteUse, string> = { operativa: '🏭', interna: '🗂️', domicilio: '📮' };

/** Dove si trova, come lo dice una persona: prima il comune, poi la via («Taranto · Via Virgilio 66»). */
export function town(s: Site): string | null {
  return [s.municipality, s.address].filter(Boolean).join(' · ') || null;
}

/**
 * Una sede nel riepilogo a conferme finite (ADR 0033, 0072): la foto o l'icona, il nome, dove si trova, poi ciò che dice
 * la visura e ciò che il cervello ha trovato sul web, e le note del titolare. Le attività stanno nella scheda, che
 * «Modifica» apre subito in modifica.
 */
function SiteRow({
  s,
  inTown,
  manager,
  open,
  edited,
  onEdit,
  editor,
}: {
  s: Site;
  inTown: number;
  manager: boolean;
  open: boolean;
  edited: boolean;
  onEdit: (() => void) | null;
  editor: ReactNode;
}) {
  const place = town(s);
  const photo = useSitePhoto(s);
  return (
    <Row open={open} edited={edited} onEdit={onEdit} editor={editor}>
      <div className="flex items-start gap-3.5">
        {photo ? (
          <Photo photo={photo} alt={s.name} credit={false} className="mt-0.5 h-16 w-16 shrink-0 rounded-xl" />
        ) : (
          <span aria-hidden className="mt-0.5 flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-paper text-2xl">
            {s.icona ?? (s.use ? USE_ICON[s.use] : '📍')}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold leading-snug">
            {titleOf(s)}
            {s.use && !operative(s) && <Pill>{USE[s.use]}</Pill>}
          </p>
          <p className="text-sm text-ink/55">{place ?? <span className="text-amber-700">Manca l’indirizzo</span>}</p>
          <SiteDetails s={s} inTown={inTown} manager={manager} />
        </div>
      </div>
    </Row>
  );
}

/** Le cose da avere, in un elenco semplice: il nome, in piccolo cosa vuol dire; al tocco il dettaglio e il perché. */
export function Needs({ items }: { items: IdealItemDto[] }) {
  return (
    <ul className="mt-2 space-y-1.5 pl-5">
      {items.map((v) => (
        <li key={v.nome} className="text-sm">
          <details>
            <summary className="cursor-pointer list-none">
              <span className="text-ink/80">○ {v.nome}</span>
              <ItemBase v={v} />
              {v.descrizione && (
                <span className="block pl-4 text-xs text-ink/50">{v.descrizione}</span>
              )}
            </summary>
            <div className="mt-1 space-y-1 pl-4 text-xs text-ink/60">
              {v.dettagli.length > 0 && (
                <ul className="list-disc pl-4">
                  {v.dettagli.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              )}
              <DutyFacts v={v} />
              {v.perche && <p className="text-ink/45">Perché: {v.perche}</p>}
              {v.riferimento && <p className="text-ink/45">Riferimento: {v.riferimento}</p>}
              <LibrarySources refs={v.libreria} fondata={v.fondata} conti={v.conti} />
            </div>
          </details>
        </li>
      ))}
    </ul>
  );
}

function Restore({ s }: { s: Site }) {
  const { t } = useTenant();
  const qc = useQueryClient();
  const back = useMutation({
    mutationFn: () => api(t(`/sites/${s.id}`), { method: 'PATCH', body: { status: 'attiva' } }),
    onSuccess: () => void qc.invalidateQueries(),
  });
  return (
    <button
      className="text-accent hover:underline disabled:opacity-50"
      disabled={back.isPending}
      onClick={() => back.mutate()}
    >
      (rimetti)
    </button>
  );
}

const INPUT =
  'w-full rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20';

/** Correggere o aggiungere una sede con le sole domande che chiunque sa rispondere. Correggere vale come conferma. */
/** `onDone` riceve la sede aggiunta (non una corretta) e se si è salvato. */
function SiteEdit({
  site,
  onDone,
}: {
  site?: Site;
  onDone: (added?: string, saved?: boolean) => void;
}) {
  const { t } = useTenant();
  const qc = useQueryClient();
  const [v, setV] = useState({
    name: site?.name ?? '',
    address: site?.address ?? '',
    postal_code: site?.postal_code ?? '',
    municipality: site?.municipality ?? '',
    province: site?.province ?? '',
    region: site?.region ?? '',
    country: site?.country ?? '',
    employees: site?.employees == null ? '' : String(site.employees),
    legal: site?.legal ?? false,
  });
  // Indirizzo dai suggerimenti di Google; i campi a mano solo se il server non ha il servizio (ADR 0028).
  const [picked, setPicked] = useState(false);
  const [manual, setManual] = useState(false);
  const save = useMutation({
    mutationFn: () => {
      type Part = 'address' | 'postal_code' | 'municipality' | 'province' | 'region' | 'country';
      const parts: Part[] = [
        'address',
        'postal_code',
        'municipality',
        'province',
        'region',
        'country',
      ];
      // Un indirizzo scelto sostituisce tutti i pezzi (anche quelli che non ha); scritto a mano completa quelli dati.
      const where = Object.fromEntries(
        parts.flatMap((k): Array<[Part, string | null]> =>
          v[k].trim() ? [[k, v[k].trim()]] : picked && site ? [[k, null]] : [],
        ),
      );
      const body = {
        name: v.name.trim(),
        ...where,
        ...(v.employees.trim() && Number.isInteger(Number(v.employees))
          ? { employees: Number(v.employees) }
          : {}),
        legal: v.legal,
        ...(site && site.status !== 'attiva' ? { status: 'attiva' } : {}),
      };
      return site
        ? api<Site>(t(`/sites/${site.id}`), { method: 'PATCH', body })
        : api<Site>(t('/sites'), { body });
    },
    onSuccess: (saved) => {
      void qc.invalidateQueries();
      onDone(site ? undefined : saved?.id, true);
    },
  });
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) =>
    setV({ ...v, [k]: e.target.value });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (v.name.trim()) save.mutate();
  };
  return (
    <form className="space-y-3" onSubmit={submit}>
      <label className="block text-sm">
        <span className="mb-1 block text-xs text-ink/50">Come la chiamate</span>
        <input
          autoFocus
          className={INPUT}
          placeholder="Es. Sede, Magazzino, Negozio di via Roma"
          value={v.name}
          onChange={set('name')}
          required
        />
      </label>
      {manual ? (
        <>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_6rem]">
            <label className="block text-sm">
              <span className="mb-1 block text-xs text-ink/50">Indirizzo</span>
              <input
                className={INPUT}
                placeholder="Via e numero"
                value={v.address}
                onChange={set('address')}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-xs text-ink/50">CAP</span>
              <input className={INPUT} value={v.postal_code} onChange={set('postal_code')} />
            </label>
          </div>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_6rem]">
            <label className="block text-sm">
              <span className="mb-1 block text-xs text-ink/50">Comune</span>
              <input className={INPUT} value={v.municipality} onChange={set('municipality')} />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-xs text-ink/50">Provincia</span>
              <input
                className={INPUT}
                placeholder="Es. MI"
                value={v.province}
                onChange={set('province')}
              />
            </label>
          </div>
        </>
      ) : (
        <div className="text-sm">
          <span className="mb-1 block text-xs text-ink/50">Dove si trova</span>
          <AddressInput
            value={addressLine({
              address: v.address || null,
              postal_code: v.postal_code || null,
              municipality: v.municipality || null,
              province: v.province || null,
            })}
            onPick={(x) => {
              setPicked(true);
              setV({
                ...v,
                address: x.address ?? '',
                postal_code: x.postal_code ?? '',
                municipality: x.municipality ?? '',
                province: x.province ?? '',
                region: x.region ?? '',
                country: x.country ?? '',
              });
            }}
            onUnavailable={() => setManual(true)}
          />
        </div>
      )}
      <label className="block text-sm">
        <span className="mb-1 block text-xs text-ink/50">
          Quante persone ci lavorano, più o meno
        </span>
        <input
          type="number"
          min={0}
          className={`${INPUT} sm:w-40`}
          value={v.employees}
          onChange={set('employees')}
        />
      </label>
      <label className="flex items-center gap-2 text-sm text-ink/75">
        <input
          type="checkbox"
          checked={v.legal}
          onChange={(e) => setV({ ...v, legal: e.target.checked })}
        />
        È la sede principale (sede legale)
      </label>
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending || !v.name.trim()}>
          Salva
        </Button>
        <Button type="button" variant="ghost" onClick={() => onDone()}>
          Annulla
        </Button>
      </div>
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
    </form>
  );
}
